import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '../button/CtyButton';
import { isRawJsonText, type ClaimSubmitErrorModalContent } from './claimSubmitErrorUtils';

export type { ClaimSubmitErrorModalContent } from './claimSubmitErrorUtils';

export function ClaimSubmitErrorModal({
  content,
  onClose,
}: Readonly<{
  content: ClaimSubmitErrorModalContent | null;
  onClose: () => void;
}>) {
  const isOpen = content != null;
  const details = (content?.details ?? []).filter((d) => d.message.trim() && !isRawJsonText(d.message));
  const rawSummary = content?.summary?.trim() ?? '';
  const summary = rawSummary && !isRawJsonText(rawSummary) ? rawSummary : '';
  const claimNumber = content?.claimNumber?.trim() ?? '';
  const heading = content?.heading?.trim() || 'Could not submit this claim';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      placement="center"
      scrollBehavior="inside"
      classNames={{ backdrop: 'bg-black/50' }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 border-b border-default-200">
          <span className="text-base font-semibold text-foreground">{heading}</span>
          {summary ? <span className="text-sm font-normal text-default-500">{summary}</span> : null}
        </ModalHeader>
        <ModalBody className="py-4">
          {claimNumber ? (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 dark:border-green-500/40 bg-green-50 dark:bg-green-500/10 px-3 py-2.5">
              <Icon icon="mdi:check-circle" width={18} className="shrink-0 text-green-600 dark:text-green-400" />
              <span className="text-sm font-semibold text-green-800 dark:text-green-300">
                Claim Number: <span className="font-bold">{claimNumber}</span>
              </span>
            </div>
          ) : null}
          {details.length > 0 ? (
            <ul className="m-0 list-none space-y-3 p-0">
              {details.map((d, i) => (
                <li
                  key={`${d.code}-${i}`}
                  className="rounded-lg border border-default-200 bg-content2/80 px-3 py-2.5 text-sm text-default-700"
                >
                  {d.code ? (
                    <span className="mr-2 inline-block rounded bg-default-100 px-1.5 py-0.5 font-mono text-xs text-default-600">
                      {d.code}
                    </span>
                  ) : null}
                  <span className="align-middle">{d.message}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </ModalBody>
        <ModalFooter className="border-t border-default-200">
          <CtyButton color="primary" className="font-semibold" onPress={onClose}>
            Close
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
